import { IDealCleanupResponseParams } from '@common/interfaces/dealCleanup'
import { ITask } from '@common/interfaces/task'
import { IApi } from '../../api'
import { createRequestTypes } from '../../helpers/helpers'
import { createAction } from '../types'

export const prefix = 'dashboard'

/** The day to read, `YYYY-MM-DD`. Omitted means today, in the server's Eastern day. */
export interface IAgendaParams {
  date?: string
  /** Keep the current day on screen while refetching it. Not sent to the server. */
  isQuiet?: boolean
}

export const [LIST_AGENDA_REQUEST, LIST_AGENDA_SUCCESS, LIST_AGENDA_FAILURE] = createRequestTypes(
  prefix,
  'LIST_AGENDA',
)

// `params.date` is load-bearing, not just metadata: the reducer keys the slice by
// it so a slow response for one day can't overwrite the day now on screen.
export const listAgenda = createAction((params: IAgendaParams) => ({
  type: [LIST_AGENDA_REQUEST, LIST_AGENDA_SUCCESS, LIST_AGENDA_FAILURE],
  promise: (api: IApi) => api.dashboard.listAgenda(params.date ? { date: params.date } : {}),
  params,
}))

/** An inclusive range of days, `YYYY-MM-DD`, at most MAX_AGENDA_RANGE_DAYS (a month view's six weeks). */
export interface ICalendarRangeParams {
  startDate: string
  endDate: string
  /** Teammates whose calendars show beside yours. */
  userIds?: string[]
  /** Keep the current events on screen while refetching, e.g. after booking a meeting. */
  isQuiet?: boolean
}

export const [
  LIST_CALENDAR_RANGE_REQUEST,
  LIST_CALENDAR_RANGE_SUCCESS,
  LIST_CALENDAR_RANGE_FAILURE,
] = createRequestTypes(prefix, 'LIST_CALENDAR_RANGE')

// Its own slice so the Calendar page and the Today card never overwrite each other.
export const listCalendarRange = createAction((params: ICalendarRangeParams) => ({
  type: [LIST_CALENDAR_RANGE_REQUEST, LIST_CALENDAR_RANGE_SUCCESS, LIST_CALENDAR_RANGE_FAILURE],
  promise: (api: IApi) =>
    api.dashboard.listAgenda({
      startDate: params.startDate,
      endDate: params.endDate,
      ...(params.userIds?.length ? { userIds: params.userIds } : {}),
    }),
  params,
}))

// Grouped by category on the server, with whole-category counts. Deliberately not
// a page of the My Tasks list — see ListMyWork.
export const [LIST_MY_WORK_REQUEST, LIST_MY_WORK_SUCCESS, LIST_MY_WORK_FAILURE] =
  createRequestTypes(prefix, 'LIST_MY_WORK')

export const listMyWork = createAction(() => ({
  type: [LIST_MY_WORK_REQUEST, LIST_MY_WORK_SUCCESS, LIST_MY_WORK_FAILURE],
  promise: (api: IApi) => api.dashboard.listMyWork(),
}))

export const [
  LIST_MY_CLIENTS_HEALTH_REQUEST,
  LIST_MY_CLIENTS_HEALTH_SUCCESS,
  LIST_MY_CLIENTS_HEALTH_FAILURE,
] = createRequestTypes(prefix, 'LIST_MY_CLIENTS_HEALTH')

export const listMyClientsHealth = createAction((params: object = {}) => ({
  type: [
    LIST_MY_CLIENTS_HEALTH_REQUEST,
    LIST_MY_CLIENTS_HEALTH_SUCCESS,
    LIST_MY_CLIENTS_HEALTH_FAILURE,
  ],
  promise: (api: IApi) => api.dashboard.listMyClientsHealth(params),
  params,
}))

export const [LIST_MY_DILIGENCE_REQUEST, LIST_MY_DILIGENCE_SUCCESS, LIST_MY_DILIGENCE_FAILURE] =
  createRequestTypes(prefix, 'LIST_MY_DILIGENCE')

export const listMyDiligence = createAction((params: object = {}) => ({
  type: [LIST_MY_DILIGENCE_REQUEST, LIST_MY_DILIGENCE_SUCCESS, LIST_MY_DILIGENCE_FAILURE],
  promise: (api: IApi) => api.dashboard.listMyDiligence(params),
  params,
}))

// The prospect widget reuses the existing prospects list (which already joins the
// CRM tables and supports the owner + lastActivityDate filters) but
// keeps its own slice: the Prospects page reads the shared `prospectsAggregation`
// slice, and a 5-row dashboard result must not stand in for that page's list.
export const [
  LIST_DASHBOARD_PROSPECTS_REQUEST,
  LIST_DASHBOARD_PROSPECTS_SUCCESS,
  LIST_DASHBOARD_PROSPECTS_FAILURE,
] = createRequestTypes(prefix, 'LIST_DASHBOARD_PROSPECTS')

export const listDashboardProspects = createAction((params: object = {}) => ({
  type: [
    LIST_DASHBOARD_PROSPECTS_REQUEST,
    LIST_DASHBOARD_PROSPECTS_SUCCESS,
    LIST_DASHBOARD_PROSPECTS_FAILURE,
  ],
  promise: (api: IApi) => api.prospect.list(params),
  params,
}))

export const [GET_BRIEFING_REQUEST, GET_BRIEFING_SUCCESS, GET_BRIEFING_FAILURE] =
  createRequestTypes(prefix, 'GET_BRIEFING')

export const getBriefing = createAction(() => ({
  type: [GET_BRIEFING_REQUEST, GET_BRIEFING_SUCCESS, GET_BRIEFING_FAILURE],
  promise: (api: IApi) => api.dashboard.getBriefing(),
}))

// Shares the briefing slice with getBriefing so a refresh replaces what's on
// screen, but drives `isSaving` rather than `isLoading` — the existing briefing
// stays visible while the new one generates instead of blanking out.
export const [REFRESH_BRIEFING_REQUEST, REFRESH_BRIEFING_SUCCESS, REFRESH_BRIEFING_FAILURE] =
  createRequestTypes(prefix, 'REFRESH_BRIEFING')

export const refreshBriefing = createAction(() => ({
  type: [REFRESH_BRIEFING_REQUEST, REFRESH_BRIEFING_SUCCESS, REFRESH_BRIEFING_FAILURE],
  promise: (api: IApi) => api.dashboard.refreshBriefing(),
}))

export interface IBriefingBulletCompletedParams {
  /** The bullet's position in the stored brief (`IArcBriefingDataBullet.index`). */
  index: number
  completed: boolean
  generatedAt: string
}

export const [
  SET_BRIEFING_BULLET_COMPLETED_REQUEST,
  SET_BRIEFING_BULLET_COMPLETED_SUCCESS,
  SET_BRIEFING_BULLET_COMPLETED_FAILURE,
] = createRequestTypes(prefix, 'SET_BRIEFING_BULLET_COMPLETED')

// `params` drives the reducer's optimistic check, and its undo if the write fails.
export const setBriefingBulletCompleted = createAction(
  (params: IBriefingBulletCompletedParams) => ({
    type: [
      SET_BRIEFING_BULLET_COMPLETED_REQUEST,
      SET_BRIEFING_BULLET_COMPLETED_SUCCESS,
      SET_BRIEFING_BULLET_COMPLETED_FAILURE,
    ],
    promise: (api: IApi) =>
      api.dashboard.setBriefingBulletCompleted(params.index, {
        completed: params.completed,
        generatedAt: params.generatedAt,
      }),
    params,
  }),
)

// The task a checked bullet points at, for the completion confirm. Not myTasks' showTask:
// that fills the open task panel's slice, which Arc's live refresh then keeps refetching.
export const [GET_BRIEFING_TASK_REQUEST, GET_BRIEFING_TASK_SUCCESS, GET_BRIEFING_TASK_FAILURE] =
  createRequestTypes(prefix, 'GET_BRIEFING_TASK')

export const getBriefingTask = createAction((id: string) => ({
  type: [GET_BRIEFING_TASK_REQUEST, GET_BRIEFING_TASK_SUCCESS, GET_BRIEFING_TASK_FAILURE],
  promise: (api: IApi): Promise<{ data: ITask }> => api.myTasks.show(id),
}))

// Client-only: a task bullet is checked off by completing its task, so its check is set here,
// at once, and undone if completing fails. Aimed at one brief, never one regenerated since.
export const MARK_BRIEFING_TASK_COMPLETED = `${prefix}/MARK_BRIEFING_TASK_COMPLETED`

export const markBriefingTaskCompleted = (params: IBriefingBulletCompletedParams) => ({
  type: MARK_BRIEFING_TASK_COMPLETED,
  params,
})

// `params.eventId` is load-bearing, not just metadata: the reducer keys the brief
// by it, which is what stops a slow response for one meeting from overwriting the
// meeting the user is actually looking at.
export const [GET_MEETING_BRIEF_REQUEST, GET_MEETING_BRIEF_SUCCESS, GET_MEETING_BRIEF_FAILURE] =
  createRequestTypes(prefix, 'GET_MEETING_BRIEF')

export const getMeetingBrief = createAction((eventId: string) => ({
  type: [GET_MEETING_BRIEF_REQUEST, GET_MEETING_BRIEF_SUCCESS, GET_MEETING_BRIEF_FAILURE],
  promise: (api: IApi) => api.dashboard.getMeetingBrief(eventId),
  params: { eventId },
}))

// Keyed by the Google event id like the read, so the regenerated brief lands in the same entry.
export const [
  REGENERATE_MEETING_BRIEF_REQUEST,
  REGENERATE_MEETING_BRIEF_SUCCESS,
  REGENERATE_MEETING_BRIEF_FAILURE,
] = createRequestTypes(prefix, 'REGENERATE_MEETING_BRIEF')

export const regenerateMeetingBrief = createAction((eventId: string, meetingId: string) => ({
  type: [
    REGENERATE_MEETING_BRIEF_REQUEST,
    REGENERATE_MEETING_BRIEF_SUCCESS,
    REGENERATE_MEETING_BRIEF_FAILURE,
  ],
  promise: (api: IApi) => api.dashboard.regenerateMeetingBrief(meetingId),
  params: { eventId },
}))

export const [
  LIST_DEAL_CLEANUP_SUGGESTIONS_REQUEST,
  LIST_DEAL_CLEANUP_SUGGESTIONS_SUCCESS,
  LIST_DEAL_CLEANUP_SUGGESTIONS_FAILURE,
] = createRequestTypes(prefix, 'LIST_DEAL_CLEANUP_SUGGESTIONS')

export const listDealCleanupSuggestions = createAction((params: object = {}) => ({
  type: [
    LIST_DEAL_CLEANUP_SUGGESTIONS_REQUEST,
    LIST_DEAL_CLEANUP_SUGGESTIONS_SUCCESS,
    LIST_DEAL_CLEANUP_SUGGESTIONS_FAILURE,
  ],
  promise: (api: IApi) => api.dashboard.listDealCleanupSuggestions(params),
  params,
}))

export const [
  RESPOND_TO_DEAL_CLEANUP_SUGGESTION_REQUEST,
  RESPOND_TO_DEAL_CLEANUP_SUGGESTION_SUCCESS,
  RESPOND_TO_DEAL_CLEANUP_SUGGESTION_FAILURE,
] = createRequestTypes(prefix, 'RESPOND_TO_DEAL_CLEANUP_SUGGESTION')

export const respondToDealCleanupSuggestion = createAction(
  (id: string, data: IDealCleanupResponseParams) => ({
    type: [
      RESPOND_TO_DEAL_CLEANUP_SUGGESTION_REQUEST,
      RESPOND_TO_DEAL_CLEANUP_SUGGESTION_SUCCESS,
      RESPOND_TO_DEAL_CLEANUP_SUGGESTION_FAILURE,
    ],
    promise: (api: IApi) => api.dashboard.respondToDealCleanupSuggestion(id, data),
  }),
)
